import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Megaphone, BarChart3, Clock, CheckCircle, XCircle, Search, RefreshCw,
  Loader2, DollarSign, TrendingUp, Eye, MousePointer, Trash2, Image,
  Store, Package, FileImage,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { adminAPI } from '../../services/api';
import { formatPrice } from '../../utils/helpers';
import { toast } from '../../hooks/use-toast';

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { key: 'approvals', label: 'Aprobaciones', icon: Clock },
  { key: 'audit', label: 'Auditoria Global', icon: Eye },
];

const PIE_COLORS = ['#F97316', '#3B82F6', '#A855F7', '#10B981'];

const TYPE_ICON = { product: Package, store: Store, banner: FileImage };
const TYPE_LABEL = { product: 'Producto', store: 'Tienda', banner: 'Banner' };

const STATUS_BADGE = {
  pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  active: 'bg-green-500/15 text-green-400 border-green-500/30',
  paused: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  rejected: 'bg-red-500/15 text-red-400 border-red-500/30',
  completed: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
};

const AdminAds = () => {
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [allAds, setAllAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'dashboard') {
        const s = await adminAPI.getAdsStats();
        setStats(s);
      } else if (tab === 'approvals') {
        const p = await adminAPI.getPendingAds();
        setPending(p);
      } else {
        const a = await adminAPI.getAllAds();
        setAllAds(a);
      }
    } catch {
      toast({ title: 'Error cargando datos', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await adminAPI.approveAd(id);
      toast({ title: 'Campana aprobada' });
      load();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setProcessing(null); }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Razon del rechazo:');
    if (!reason) return;
    setProcessing(id);
    try {
      await adminAPI.rejectAd(id, reason);
      toast({ title: 'Campana rechazada' });
      load();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setProcessing(null); }
  };

  const filtered = allAds.filter(a => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (a.user?.name || '').toLowerCase().includes(q) ||
           (a.user?.email || '').toLowerCase().includes(q) ||
           (a.product?.title || '').toLowerCase().includes(q) ||
           (a.ad_type || '').includes(q);
  });

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-ads">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-orange-500/20 to-amber-600/20 rounded-xl border border-orange-700/30">
              <Megaphone className="w-7 h-7 text-orange-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="admin-ads-title">Zona Libre Ads</h1>
              <p className="text-gray-400 text-sm">War Room Publicitario</p>
            </div>
          </div>
          <Button variant="ghost" onClick={load} className="text-gray-400 hover:text-white">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-800 rounded-xl p-1" data-testid="ads-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  tab === t.key ? 'bg-gray-700 text-white shadow' : 'text-gray-400 hover:text-gray-200'
                }`} data-testid={`tab-${t.key}`}>
                <Icon className="w-4 h-4" /> {t.label}
                {t.key === 'approvals' && pending.length > 0 && tab !== 'approvals' && (
                  <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] flex items-center justify-center">{pending.length}</span>
                )}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : (
          <>
            {/* ═══ DASHBOARD TAB ═══ */}
            {tab === 'dashboard' && stats && (
              <div className="space-y-6">
                {/* KPIs */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-green-500/10 border-green-700/30 p-4">
                    <DollarSign className="w-6 h-6 text-green-400 mb-2" />
                    <p className="text-xs text-green-400/70 uppercase">Ingresos Totales</p>
                    <p className="text-2xl font-bold text-green-300">{formatPrice(stats.total_revenue)}</p>
                  </Card>
                  <Card className="bg-orange-500/10 border-orange-700/30 p-4">
                    <TrendingUp className="w-6 h-6 text-orange-400 mb-2" />
                    <p className="text-xs text-orange-400/70 uppercase">Gasto Programado</p>
                    <p className="text-2xl font-bold text-orange-300">{formatPrice(stats.scheduled_spend)}</p>
                  </Card>
                  <Card className="bg-blue-500/10 border-blue-700/30 p-4">
                    <Megaphone className="w-6 h-6 text-blue-400 mb-2" />
                    <p className="text-xs text-blue-400/70 uppercase">Campanas Activas</p>
                    <p className="text-2xl font-bold text-blue-300">{stats.active_count}</p>
                  </Card>
                  <Card className="bg-amber-500/10 border-amber-700/30 p-4">
                    <Clock className="w-6 h-6 text-amber-400 mb-2" />
                    <p className="text-xs text-amber-400/70 uppercase">Pendientes</p>
                    <p className="text-2xl font-bold text-amber-300">{stats.pending_count}</p>
                  </Card>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Card className="bg-gray-800 border-gray-700 p-5 lg:col-span-2" data-testid="daily-revenue-chart">
                    <h3 className="text-sm font-semibold text-gray-300 mb-4">Ingresos Diarios (7 dias)</h3>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={stats.daily_revenue}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" tick={{ fill: '#9CA3AF', fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                        <YAxis tick={{ fill: '#9CA3AF', fontSize: 11 }} tickFormatter={v => `$${v}`} />
                        <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: 8 }} labelStyle={{ color: '#9CA3AF' }} formatter={v => [`$${v}`, 'Ingresos']} />
                        <Bar dataKey="revenue" fill="#F97316" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>

                  <Card className="bg-gray-800 border-gray-700 p-5" data-testid="type-revenue-chart">
                    <h3 className="text-sm font-semibold text-gray-300 mb-4">Ingresos por Tipo</h3>
                    {stats.revenue_by_type.length > 0 ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={stats.revenue_by_type} dataKey="revenue" nameKey="type" cx="50%" cy="50%"
                            innerRadius={50} outerRadius={80} paddingAngle={3}>
                            {stats.revenue_by_type.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: 8 }} formatter={v => `$${v}`} />
                          <Legend formatter={v => TYPE_LABEL[v] || v} wrapperStyle={{ fontSize: 12, color: '#9CA3AF' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <p className="text-gray-500 text-center py-12">Sin datos</p>}
                  </Card>
                </div>
              </div>
            )}

            {/* ═══ APPROVALS TAB ═══ */}
            {tab === 'approvals' && (
              <div className="space-y-4" data-testid="approvals-list">
                {pending.length === 0 ? (
                  <Card className="bg-gray-800 border-gray-700 p-12 text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3 opacity-50" />
                    <p className="text-gray-400 text-lg">Sin campanas pendientes</p>
                  </Card>
                ) : pending.map((c) => {
                  const TIcon = TYPE_ICON[c.ad_type] || Package;
                  return (
                    <Card key={c.campaign_id || c._id} className="bg-gray-800 border-gray-700 p-5" data-testid={`pending-${c.campaign_id || c._id}`}>
                      <div className="flex items-start gap-4">
                        {/* Image/icon */}
                        <div className="w-16 h-16 rounded-lg bg-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {c.banner_image_url ? (
                            <img src={c.banner_image_url} alt="" className="w-full h-full object-cover" />
                          ) : c.product?.images?.[0] ? (
                            <img src={c.product.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <TIcon className="w-7 h-7 text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <TIcon className="w-4 h-4 text-orange-400" />
                            <span className="text-xs text-orange-400 font-medium uppercase">{TYPE_LABEL[c.ad_type] || c.ad_type}</span>
                          </div>
                          <p className="text-white font-semibold truncate">{c.product?.title || c.target_url || `Campana ${c.ad_type}`}</p>
                          <p className="text-gray-500 text-xs">{c.user_name || '—'} · {new Date(c.created_at).toLocaleDateString('es-MX')}</p>
                          <p className="text-gray-400 text-sm mt-1">Presupuesto: <span className="text-white font-mono">{formatPrice(c.budget)}</span></p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button size="sm" onClick={() => handleApprove(c.campaign_id || c._id)}
                            disabled={processing === (c.campaign_id || c._id)}
                            className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-${c.campaign_id || c._id}`}>
                            {processing === (c.campaign_id || c._id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> Aprobar</>}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleReject(c.campaign_id || c._id)}
                            className="text-red-400 hover:text-red-300" data-testid={`reject-${c.campaign_id || c._id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Rechazar
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* ═══ AUDIT TAB ═══ */}
            {tab === 'audit' && (
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Buscar por usuario, producto o tipo..." className="bg-gray-800 border-gray-700 text-white pl-10" />
                </div>
                <Card className="bg-gray-800 border-gray-700 overflow-hidden" data-testid="audit-table">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-700 bg-gray-900/50">
                          <th className="text-left p-3 text-gray-400 font-medium">Campana</th>
                          <th className="text-left p-3 text-gray-400 font-medium">Tipo</th>
                          <th className="text-left p-3 text-gray-400 font-medium">Anunciante</th>
                          <th className="text-right p-3 text-gray-400 font-medium">Budget</th>
                          <th className="text-right p-3 text-gray-400 font-medium">Impresiones</th>
                          <th className="text-right p-3 text-gray-400 font-medium">Clicks</th>
                          <th className="text-center p-3 text-gray-400 font-medium">Estado</th>
                          <th className="text-left p-3 text-gray-400 font-medium">Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.length === 0 ? (
                          <tr><td colSpan={8} className="p-8 text-center text-gray-500">Sin campanas</td></tr>
                        ) : filtered.map((c, i) => {
                          const TIcon = TYPE_ICON[c.ad_type] || Package;
                          return (
                            <tr key={i} className={`border-b border-gray-700/50 hover:bg-gray-700/30 ${c.is_deleted ? 'opacity-60' : ''}`} data-testid={`audit-row-${i}`}>
                              <td className="p-3">
                                <p className="text-white font-medium truncate max-w-[180px]">{c.product?.title || c.target_url || `Campana ${c.ad_type}`}</p>
                              </td>
                              <td className="p-3">
                                <span className="inline-flex items-center gap-1 text-xs">
                                  <TIcon className="w-3 h-3 text-orange-400" />
                                  <span className="text-gray-300">{TYPE_LABEL[c.ad_type] || c.ad_type}</span>
                                </span>
                              </td>
                              <td className="p-3">
                                <p className="text-gray-300 text-xs">{c.user?.name || '—'}</p>
                                <p className="text-gray-500 text-[10px]">{c.user?.email || ''}</p>
                              </td>
                              <td className="p-3 text-right font-mono text-green-400">{formatPrice(c.budget)}</td>
                              <td className="p-3 text-right font-mono text-gray-300">{(c.impressions || 0).toLocaleString()}</td>
                              <td className="p-3 text-right font-mono text-gray-300">{c.clicks || 0}</td>
                              <td className="p-3 text-center">
                                <div className="flex flex-col items-center gap-1">
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${STATUS_BADGE[c.status] || 'bg-gray-600 text-gray-300'}`}>
                                    {c.status}
                                  </span>
                                  {c.is_deleted && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-0.5">
                                      <Trash2 className="w-2.5 h-2.5" /> Eliminado
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-3 text-gray-500 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString('es-MX') : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAds;
